/**
 * The customer line for a menu-module error (HITL-1699).
 *
 * The agent-facing copy (`menuModuleError.ts`) names the failing node by its
 * brand-menu name, which is sometimes a Presto-internal name (`mint mod`,
 * `explicit blizzard choice`) that must never be spoken. This module turns the
 * same `ICartError`s into what the customer hears, offered as the Omnibar's
 * next prompt.
 *
 * WHICH NAME TO SPEAK, per failing node:
 *   1. An `unavailable_brand_items` entry names what failed in
 *      `unavailableOptions` (`banana blizzard`); the node path ends at the
 *      item that OWNS that choice (`blizzard`), so the option wins.
 *   2. Otherwise, the brand menu's `readback_name` says whether the item name
 *      is speakable — the same field Lucy's readback renders. A template that
 *      uses `{ITEM}` reuses the name; one without it was written to REPLACE
 *      the name, so its literal words are the spoken name
 *      (`{blizzard_flavor} blizzard(s) /w …` → `blizzard`).
 *   3. Otherwise the raw item name, which is already right for most items.
 *
 * Lucy's full template rendering is deliberately not reproduced: those
 * templates are written for the whole-order readback, where a parent renders
 * its children, so rendering one node alone gives a combo's drink as just its
 * size. Templates with `pre(…)` or `<…>` logic only Lucy can evaluate, so they
 * fall back to the raw name.
 */

import { CartErrorReason } from '../constants/brandMenu';
import { ICartError, IMUItem } from '../types/brandMenu';
import { getOrderErrorMessage } from './errors';
import { GenericMap } from './types';

const hasTemplateLogic = (template: string): boolean =>
  /pre\(/.test(template) || /<[^<>]*>/.test(template);

/** The words of a readback template that are spoken as-is: the part before
 * the "with …" section (`/w`) or the first multi-part group (`[[`), with
 * `{…}` placeholders, plural `(s)`, suppress/enforce prefixes and
 * `:p0`/`:s0` position configs removed. */
const literalWordsOf = (template: string): string =>
  template
    .split(/\/w|\[\[/)[0]
    .replace(/\{[^{}]*\}/g, ' ')
    .replace(/\(s\)/g, '')
    .replace(/!!|\^\^/g, '')
    .replace(/:[ps]\d\b/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .join(' ')
    .replace(/^[\s,]+|[\s,]+$/g, '');

/** Rules 2 and 3 above: the name to speak for a brand-menu item. */
export const getSpokenItemName = (
  name: string,
  brandMenuItemsByName?: GenericMap<IMUItem>
): string => {
  const readbackName = brandMenuItemsByName?.[name]?.readback_name;
  if (typeof readbackName !== 'string') return name;

  const template = readbackName.trim();
  if (
    !template ||
    template === '{}' ||
    template.includes('{ITEM}') ||
    hasTemplateLogic(template)
  ) {
    return name;
  }
  return literalWordsOf(template) || name;
};

const namesOf = (
  error: ICartError,
  brandMenuItemsByName?: GenericMap<IMUItem>
): string[] => {
  if (error.unavailableOptions?.length) return error.unavailableOptions;
  const leaf = error.nodePath[error.nodePath.length - 1];
  return leaf ? [getSpokenItemName(leaf, brandMenuItemsByName)] : [];
};

const uniqueNames = (names: string[], exclude: string[] = []): string[] => {
  const seen = new Set(exclude.map((name) => name.toLowerCase()));
  return names.filter((name) => {
    const key = name.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** "a", "a and b", "a, b and c". */
const joinNames = (names: string[]): string =>
  names.length <= 1
    ? names[0] ?? ''
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

const isNotAvailable = (names: string[]): string =>
  `${joinNames(names)} ${
    names.length > 1 ? "aren't" : "isn't"
  } available at this location`;

const FOLLOW_UP = 'Would you like something else?';

const isPromptCarrier = (name: string): boolean => name.startsWith('/');

/**
 * The item a non-availability error (unmatched, invalid, general) is about,
 * as the customer ordered it: the failing CART LINE, not the node the path
 * ends at. Those paths often end at an option (`no combo`, `decline`,
 * `crispy`), which means nothing to the customer.
 *   - The cart line the order handler matched the error to, when it did.
 *   - Otherwise the path's first entry, the top-level item (AI hypothesis
 *     errors are matched by that name and carry no cart id).
 *   - Nothing for a bare `unmatched` name that matched no cart line — it may
 *     be a nested option — or for a slash prompt carrier (`/drink:`).
 */
const orderedItemNameOf = (
  error: ICartError,
  cartItems?: GenericMap<{ name?: string }>
): string | undefined => {
  const cartLineName =
    error.cartItemId !== undefined
      ? cartItems?.[error.cartItemId]?.name
      : undefined;
  const name =
    cartLineName ||
    (error.nodePath.length > 1 ||
    (error.nodePath.length === 1 &&
      error.reasonCode !== CartErrorReason.unmatched)
      ? error.nodePath[0]
      : undefined);
  return name && !isPromptCarrier(name) ? name : undefined;
};

/**
 * What to say to the customer for the current menu-module errors, or
 * undefined when there are none. Out-of-stock and not-sold items get their
 * own wording and are all named in one line. Any other error type (unmatched,
 * invalid, general, unknown) names the ordered item it failed on — "I'm having
 * trouble adding your chicken strip sandwich." — or says "that item" when it
 * can't tell, and is used only when no availability error is present.
 */
export const getMenuModuleErrorCustomerLine = (
  cartErrors: ICartError[] | undefined,
  brandMenuItemsByName?: GenericMap<IMUItem>,
  cartItems?: GenericMap<{ name?: string }>
): string | undefined => {
  if (!cartErrors?.length) return undefined;

  const namesFor = (reason: CartErrorReason) =>
    cartErrors
      .filter((error) => error.reasonCode === reason)
      .flatMap((error) => namesOf(error, brandMenuItemsByName));

  const outOfStock = uniqueNames(namesFor(CartErrorReason.outOfStock));
  const notSold = uniqueNames(namesFor(CartErrorReason.notSold), outOfStock);

  if (outOfStock.length && notSold.length) {
    return `Sorry, we're out of ${joinNames(outOfStock)}, and ${isNotAvailable(
      notSold
    )}. ${FOLLOW_UP}`;
  }
  if (outOfStock.length) {
    return `Sorry, we're out of ${joinNames(
      outOfStock
    )} right now. ${FOLLOW_UP}`;
  }
  if (notSold.length) {
    return `Sorry, ${isNotAvailable(notSold)}. ${FOLLOW_UP}`;
  }

  const orderedItems = uniqueNames(
    cartErrors
      .map((error) => orderedItemNameOf(error, cartItems))
      .filter((name): name is string => !!name)
      .map((name) => getSpokenItemName(name, brandMenuItemsByName))
  );
  return orderedItems.length
    ? `I'm having trouble adding your ${joinNames(orderedItems)}.`
    : getOrderErrorMessage(true);
};

/** Identifies one set of errors, so the Omnibar offers its line once and
 * offers again only when the errors change. */
export const getMenuModuleErrorsKey = (
  cartErrors: ICartError[] | undefined
): string =>
  (cartErrors ?? [])
    .map(
      (error) =>
        `${error.reasonCode}:${error.nodePath.join('>')}:${(
          error.unavailableOptions ?? []
        ).join(',')}`
    )
    .join('|');
